import React, { MouseEventHandler, MouseEvent } from 'react';
import {
    StyledChip,
    TitleWrapper,
    LabelWrapper,
    ContentWrapper
} from './HBChip.styles';
import HBText from '../HBText/HBText';
import type { IHBChipProps, TIconPlacement } from './HBChip.types';
import { CHIP_STRUCTURE } from './HBChip.structure';
import { getChipColors } from './HBChip.colors';
import { IconComponent } from '@honeybook/icons';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';

export const HBChip = ({
    size = 'medium',
    theme = 'neutral',
    selected = false,
    disabled = false,
    prefix,
    suffix,
    prefixIcon: PrefixIcon,
    suffixIcon: SuffixIcon,
    title,
    onClick,
    label,
    maxWidth,
    testIdName,
    testId,
    testIdType,
    customStyles
}: IHBChipProps & MustIncludeOneOfBasicTestIdWithIdProps) => {
    const { text: textColor } = getChipColors(theme, selected, disabled);
    const textType = CHIP_STRUCTURE[size].textType;

    // Without an onClick the chip is display-only: leave the click alone so a
    // parent anchor can navigate, and let HBClickable skip button semantics.
    const handleClick: MouseEventHandler<HTMLDivElement> | undefined = onClick
        ? (e: MouseEvent<HTMLDivElement>) => {
              e.preventDefault();
              onClick(e);
          }
        : undefined;

    const renderIcon = (
        Icon: IconComponent | undefined,
        placement: TIconPlacement
    ) => {
        if (!Icon) {
            return null;
        }

        return (
            <ContentWrapper
                $size={size}
                $iconColor={textColor}
                $placement={placement}
            >
                <Icon />
            </ContentWrapper>
        );
    };

    const renderPrefix = () => {
        if (prefix) {
            return (
                <ContentWrapper
                    $placement="prefix"
                    $size={size}
                    $iconColor={textColor}
                >
                    {prefix}
                </ContentWrapper>
            );
        }
        return renderIcon(PrefixIcon, 'prefix');
    };

    const renderSuffix = () => {
        if (suffix) {
            return (
                <ContentWrapper
                    $placement="suffix"
                    $size={size}
                    $iconColor={textColor}
                >
                    {suffix}
                </ContentWrapper>
            );
        }
        return renderIcon(SuffixIcon, 'suffix');
    };

    return (
        <StyledChip
            isDisabled={disabled}
            onClick={handleClick}
            testId={testId}
            testIdName={testIdName}
            testIdType={testIdType}
            $size={size}
            $maxWidth={maxWidth}
            $selected={selected}
            $theme={theme}
            $customStyles={customStyles?.chip}
        >
            {renderPrefix()}

            {title && (
                <TitleWrapper
                    $customStyles={customStyles?.title}
                    $hasMaxWidth={!!maxWidth}
                >
                    <HBText weight={600} ellipsis={!!maxWidth} type={textType}>
                        {title}
                    </HBText>
                </TitleWrapper>
            )}

            <LabelWrapper
                $customStyles={customStyles?.label}
                $hasMaxWidth={!!maxWidth}
            >
                <HBText ellipsis={!!maxWidth} type={textType}>
                    {label}
                </HBText>
            </LabelWrapper>

            {renderSuffix()}
        </StyledChip>
    );
};

export default HBChip;
